import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Pencil, School } from "lucide-react";
import { Modal } from "src/components/ui/Modal";
import EditSectionModal from "src/components/student/profile/EditSectionModal";
import { FormInput } from "src/components/ui/FormInput";
import { StudentExtendedProfileData } from "src/pages/student/Profile";

interface EducationSectionProps {
  profileData: StudentExtendedProfileData;
  editData: Partial<StudentExtendedProfileData>;
  activeSectionEdit: string | null;
  setActiveSectionEdit: (section: string | null) => void;
  updateEditData: (field: string, value: any) => void;
  updateProfile: (data: Partial<StudentExtendedProfileData>) => Promise<void>;
  getEducationLevelText: (level: string) => string;
}

const EducationSection: React.FC<EducationSectionProps> = ({
  profileData,
  editData,
  activeSectionEdit,
  setActiveSectionEdit,
  updateEditData,
  updateProfile,
  getEducationLevelText
}) => {
  return (
    <>
      {/* Education Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Education</CardTitle>
          <button
            onClick={() => setActiveSectionEdit('education')}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Edit Education"
          >
            <Pencil className="h-4 w-4 text-gray-500" />
          </button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <School className="h-6 w-6 text-gray-500" />
              </div>
              <div>
                <h3 className="font-medium">{profileData.academicHistory.schoolName || "School not specified"}</h3>
                <p className="text-md" style={{ color: '#ff7f52' }}>
                  {getEducationLevelText(profileData.educationLevel) || 'Grade not specified'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Education Edit Modal */}
      {activeSectionEdit === 'education' && (
        <Modal
          isOpen={activeSectionEdit === 'education'}
          onClose={() => setActiveSectionEdit(null)}
          title="Edit Education"
        >
          <div className="space-y-5">
            <FormInput
              label="School Name"
              id="schoolName"
              placeholder="Enter your school name"
              helpText="The name of your current school or institution"
              value={editData.academicHistory?.schoolName || ""}
              onChange={(e) => updateEditData("academicHistory", {
                ...editData.academicHistory,
                schoolName: e.target.value
              })}
              labelClassName="text-xs font-semibold text-gray-600 uppercase tracking-wide"
              valueClassName="font-nunito font-bold text-gray-900 text-lg"
            />
            <FormInput
              label="Education Level"
              id="educationLevel"
              placeholder="Enter your highest pursuing grade"
              helpText="Your current grade or education level"
              value={editData.educationLevel || ""}
              onChange={(e) => updateEditData("educationLevel", e.target.value)}
              labelClassName="text-xs font-semibold text-gray-600 uppercase tracking-wide"
              valueClassName="font-nunito font-bold text-gray-900 text-lg"
            />
          </div>
          <div className="flex flex-row w-full gap-2 mt-6 justify-between">
            <Button variant="outline" onClick={() => setActiveSectionEdit(null)} className="w-32">
              Cancel
            </Button>
            <Button onClick={() => {
              updateProfile(editData);
              setActiveSectionEdit(null);
            }} className="w-32">
              OK
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default EducationSection;
